import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    CircularProgress,
} from '@material-ui/core';
import { postCmiSimulacrosIAAnalisisVariacion } from '../../../services/alumnos';
import ReactDiffViewer, { DiffMethod } from 'react-diff-viewer';

const stripHtml = (html) => {
    if (!html) return '';
    return html
        // Etiquetas de bloque → salto de línea
        .replace(/<\/?(p|div|li|ul|ol|h[1-6]|blockquote|tr|td|th)[^>]*>/gi, '\n')
        // <br> → salto de línea
        .replace(/<br\s*\/?>/gi, '\n')
        // Resto de etiquetas → vacío
        .replace(/<[^>]+>/g, '')
        // Entidades HTML básicas
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        // Más de dos saltos de línea consecutivos → dos
        .replace(/\n{3,}/g, '\n\n')
        .trim();
};

const getZone = (v) => {
    if (v < 20) return '#15803D';
    if (v < 50) return '#D97706';
    return '#DC2626';
};

const buildDownloadText = (correccion) => {
    const valoracion = stripHtml(correccion.valoracion_tutor);
    const correccionIA = stripHtml(correccion.ultima_correccion_ia);

    return `ANÁLISIS DE DIFERENCIAS ENTRE CORRECCIÓN TUTOR E IA
=====================================================
Tutor: ${correccion.tutor}
Alumno: ${correccion.alumno_nombre}
Descripción: ${correccion.descripcion}
Tipo: ${correccion.tipo}
Variación de texto: ${Number(correccion.similar_text_variacion).toFixed(1)}%

INSTRUCCIONES PARA EL MODELO
-----------------------------
Se presentan dos versiones de la corrección de un mismo ejercicio:
- La "Valoración del tutor" es la corrección humana.
- La "Corrección de la IA" es la generada automáticamente.
Analiza las diferencias entre ambas, identifica discrepancias relevantes,
puntos en los que el tutor añade valor y aspectos en los que la IA puede mejorar.

VALORACIÓN DEL TUTOR
--------------------
${valoracion || '(vacío)'}

CORRECCIÓN DE LA IA
-------------------
${correccionIA || '(vacío)'}
`;
};

const downloadCorreccion = (correccion) => {
    const text = buildDownloadText(correccion);
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `correccion_${correccion.entrega_id || correccion.alumno_nombre || 'detalle'}.txt`;
    a.click();
    URL.revokeObjectURL(url);
};

export default function DialogDiffCorreccion({ openModal, setOpenModal, correccion }) {
    const [loadingAnalisis, setLoadingAnalisis] = useState(false);
    const [resultadoAnalisis, setResultadoAnalisis] = useState(null);

    useEffect(() => {
        setResultadoAnalisis(null);
        setLoadingAnalisis(false);
    }, [correccion, openModal]);

    const handleObtenerAnalisis = async () => {
        setLoadingAnalisis(true);
        setResultadoAnalisis(null);
        const analisis = buildDownloadText(correccion);
        const resp = await postCmiSimulacrosIAAnalisisVariacion({ analisis });
        setResultadoAnalisis(resp?.resultado ?? null);
        setLoadingAnalisis(false);
    };

    return (
        <Dialog
            open={openModal}
            onClose={() => setOpenModal(false)}
            fullWidth
            maxWidth="lg"
        >
            {correccion && (
                <>
                    <DialogTitle disableTypography>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                            <Typography variant="h6" style={{ fontWeight: 600, flexShrink: 0 }}>
                                Detalle de corrección
                            </Typography>
                            <Typography variant="body2" style={{ color: '#6B7280' }}>
                                {correccion.entrega_id}
                            </Typography>
                            {correccion.tutor && (
                                <Typography variant="body2" style={{ color: '#374151' }}>
                                    · {correccion.tutor}
                                </Typography>
                            )}
                            {correccion.descripcion && (
                                <Typography variant="body2" style={{ color: '#374151', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    · {correccion.descripcion}
                                </Typography>
                            )}
                            <span style={{
                                marginLeft: 'auto',
                                padding: '2px 10px',
                                borderRadius: 12,
                                fontSize: 13,
                                fontWeight: 700,
                                color: '#fff',
                                background: getZone(correccion.similar_text_variacion),
                                flexShrink: 0,
                            }}>
                                {Number(correccion.similar_text_variacion).toFixed(1)}%
                            </span>
                        </div>
                    </DialogTitle>

                    <DialogContent dividers style={{ padding: 0 }}>
                        <ReactDiffViewer
                            oldValue={stripHtml(correccion.valoracion_tutor)}
                            newValue={stripHtml(correccion.ultima_correccion_ia)}
                            splitView={true}
                            leftTitle="Valoración tutor"
                            rightTitle="Última corrección IA"
                            compareMethod={DiffMethod.WORDS}
                            useDarkTheme={false}
                            hideLineNumbers={false}
                        />
                    </DialogContent>

                    {resultadoAnalisis && (
                        <div style={{ borderTop: '2px solid #E5E7EB', background: '#F8FAFC' }}>
                            <div style={{
                                padding: '10px 24px',
                                background: '#EFF6FF',
                                borderBottom: '1px solid #BFDBFE',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                            }}>
                                <span style={{ fontSize: 15, fontWeight: 700, color: '#1D4ED8' }}>
                                    Análisis IA
                                </span>
                            </div>
                            <div
                                style={{
                                    padding: '20px 28px',
                                    maxHeight: 400,
                                    overflowY: 'auto',
                                    color: '#1F2937',
                                    fontSize: 14,
                                    lineHeight: 1.7,
                                }}
                                dangerouslySetInnerHTML={{ __html: resultadoAnalisis.replace(/\n/g, '<br>') }}
                            />
                        </div>
                    )}

                    <DialogActions>
                        <Button
                            onClick={() => downloadCorreccion(correccion)}
                            color="primary"
                            variant="contained"
                            style={{ marginRight: 'auto' }}
                        >
                            Descargar para LLM
                        </Button>
                        <Button
                            onClick={handleObtenerAnalisis}
                            color="secondary"
                            variant="contained"
                            disabled={loadingAnalisis}
                            style={{ marginRight: 8 }}
                            startIcon={loadingAnalisis ? <CircularProgress size={16} color="inherit" /> : null}
                        >
                            {loadingAnalisis ? 'Analizando...' : 'Obtener análisis'}
                        </Button>
                        <Button onClick={() => setOpenModal(false)} color="primary" variant="outlined">
                            Cerrar
                        </Button>
                    </DialogActions>
                </>
            )}
        </Dialog>
    );
}
